import { useState, useCallback, useMemo } from 'react';

import {
  useGetBalanceSheet,
  BalanceSheetFilter,
  BalanceSheetApiResponse,
} from '../../api/accounts';
import { useAppContext } from '../../contexts/Storage';

interface UseBalanceSheetOptions {
  recordId?: number;
}

// Helper to get default date range (1 year back to today)
const getDefaultDateRange = () => {
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    start_date: `${formatDate(oneYearAgo)} 00:00:00`,
    end_date: `${formatDate(today)} 23:59:59`,
  };
};

// Types for mapped balance sheet data
export interface BalanceSheetItem {
  name: string;
  amount: number;
  isSubItem?: boolean;
}

export interface BalanceSheetSection {
  title: string;
  items: BalanceSheetItem[];
  subTotal?: number;
}

export interface MappedBalanceSheetData {
  debitSide: {
    sections: BalanceSheetSection[];
    total: number;
  };
  creditSide: {
    sections: BalanceSheetSection[];
    total: number;
  };
}

const useBalanceSheet = (options: UseBalanceSheetOptions = {}) => {
  const { recordId: initialRecordId } = options;
  const { user, selectedRecord } = useAppContext();

  // Use provided recordId or fall back to selected record
  const recordId = initialRecordId ?? selectedRecord?.id;
  const userId = user?.id;

  // Get default date range
  const defaultDates = getDefaultDateRange();

  // Filter state - include default dates as API requires them
  const [filters, setFilters] = useState<Partial<BalanceSheetFilter>>({
    start_date: defaultDates.start_date,
    end_date: defaultDates.end_date,
  });

  // API hook - only fetch when we have required params
  const hasRequiredParams = !!recordId && !!userId && !!filters.start_date && !!filters.end_date;
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetBalanceSheet({
    filters: {
      ...filters,
      record_id: recordId,
      user_id: userId,
    },
    enabled: hasRequiredParams,
  });

  // Raw data
  const rawData = useMemo(() => data?.data, [data?.data]);

  // Helper to capitalize first letter
  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

  // Map API response to display format
  const mappedData = useMemo((): MappedBalanceSheetData | null => {
    if (!rawData) return null;

    const response = rawData as BalanceSheetApiResponse;

    // Process Assets (Debit Side)
    const debitSections: BalanceSheetSection[] = [];
    let debitTotal = 0;

    if (response.assets) {
      const assetsSection: BalanceSheetSection = {
        title: 'Assets:',
        items: [],
      };

      Object.entries(response.assets).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          assetsSection.items.push({ name: `${capitalize(key)}:`, amount: 0 });
          
          Object.entries(value as Record<string, number>).forEach(([itemName, itemAmount]) => {
            assetsSection.items.push({ name: itemName, amount: itemAmount, isSubItem: true });
            debitTotal += itemAmount;
          });
        } else if (typeof value === 'number') {
          assetsSection.items.push({ name: capitalize(key), amount: value });
          debitTotal += value;
        }
      });

      debitSections.push(assetsSection);
    }

    // Process Liabilities (Credit Side)
    const creditSections: BalanceSheetSection[] = [];
    let creditTotal = 0;

    if (response.liabilities) {
      const liabilitiesSection: BalanceSheetSection = {
        title: 'Liabilities:',
        items: [],
      };

      Object.entries(response.liabilities).forEach(([name, amount]) => {
        liabilitiesSection.items.push({ name, amount, isSubItem: true });
        creditTotal += amount;
      });

      creditSections.push(liabilitiesSection);
    }

    // Capital section
    if (response.capital) {
      const capitalSection: BalanceSheetSection = {
        title: '',
        items: [],
      };

      Object.entries(response.capital).forEach(([name, amount]) => {
        capitalSection.items.push({
          name: name === 'capital' ? 'Capital' : name,
          amount,
        });
        creditTotal += amount;
      });

      if (capitalSection.items.length > 0) {
        creditSections.push(capitalSection);
      }
    }

    return {
      debitSide: {
        sections: debitSections,
        total: debitTotal,
      },
      creditSide: {
        sections: creditSections,
        total: creditTotal,
      },
    };
  }, [rawData]);

  // Filter handlers
  const updateFilter = useCallback((updates: Partial<BalanceSheetFilter>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleDateFilter = useCallback((startDate?: string, endDate?: string) => {
    const defaults = getDefaultDateRange();
    updateFilter({
      start_date: startDate ? `${startDate} 00:00:00` : defaults.start_date,
      end_date: endDate ? `${endDate} 23:59:59` : defaults.end_date,
    });
  }, [updateFilter]);

  const resetFilters = useCallback(() => {
    const defaultDates = getDefaultDateRange();
    setFilters({
      start_date: defaultDates.start_date,
      end_date: defaultDates.end_date,
    });
  }, []);

  return {
    // Data
    rawData,
    mappedData,

    // Loading states
    isLoading,
    hasRequiredParams,

    // Error state
    error,

    // Filter state and handlers
    filters,
    handleDateFilter,
    resetFilters,
    updateFilter,

    // Refetch
    refetch,
  };
};

export default useBalanceSheet;
